import { ChevronDown, Globe } from 'lucide-react';
import {
  LOCALE_LABELS,
  LOCALE_SHORT_LABELS,
  SUPPORTED_LOCALES,
  type Locale,
} from '../intl/index.ts';

// Visible language toggle, mirrored from the buyer portal. `locale` state is
// lifted to the root layout so it drives the IntlProvider.
//
// On narrow viewports the control collapses to a compact "PT"/"EN" pill so the
// long label ("Português (Brasil)") can't push the header CTA off-screen. The
// real <select> stays in the DOM — transparent and overlaid on the pill — so the
// native picker, full-name options, and accessible name are all preserved. From
// `sm` up it becomes the normal full-width select again.
export function LanguageSwitcher({
  locale,
  onChange,
  label,
}: {
  locale: Locale;
  onChange: (next: Locale) => void;
  label: string;
}) {
  return (
    <label className="relative flex items-center gap-2 text-sm text-[var(--color-navy-700)]">
      <Globe size={16} strokeWidth={1.5} aria-hidden="true" className="shrink-0" />
      <span className="sr-only">{label}</span>

      <select
        value={locale}
        onChange={(e) => onChange(e.target.value as Locale)}
        aria-label={label}
        className="peer absolute inset-0 cursor-pointer opacity-0 sm:static sm:rounded-md sm:border sm:border-[var(--color-navy-200)] sm:bg-transparent sm:px-2 sm:py-1 sm:opacity-100"
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option key={l} value={l}>
            {LOCALE_LABELS[l]}
          </option>
        ))}
      </select>

      {/* Compact display for the closed control on mobile; hidden from `sm` up,
          where the select renders its own value. It overlays the transparent
          select above (which keeps the native picker) and stays click-through
          via `pointer-events-none`; ordered after the select so the pill can
          reflect keyboard focus via `peer-focus-visible`. */}
      <span
        aria-hidden="true"
        className="pointer-events-none inline-flex items-center gap-1 rounded-md border border-[var(--color-navy-200)] px-2 py-1 font-medium peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-navy-300)] sm:hidden"
      >
        {LOCALE_SHORT_LABELS[locale]}
        <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
      </span>
    </label>
  );
}
